import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import * as tf from '@tensorflow/tfjs';
import * as faceLandmarksDetection from '@tensorflow-models/face-landmarks-detection';
import { BehaviorSubject, Observable } from 'rxjs';
import { Sprite, Texture, Container, Graphics } from 'pixi.js';
import { LogService } from 'app/services/logging.service';

/**
 * Service for face landmarks detection and face filter effects
 */
@Injectable({
  providedIn: 'root',
})
export class FaceLandmarksService implements OnDestroy {
  private ngZone = inject(NgZone);
  private logService = inject(LogService);

  // The loaded face detection model
  private detector: faceLandmarksDetection.FaceLandmarksDetector | null = null;

  // Flag to track if the model is loading
  private isLoading = false;

  // Subject to track model loading state
  private modelLoaded$ = new BehaviorSubject<boolean>(false);

  // Tracking available face landmarks
  private currentFaceLandmarks: faceLandmarksDetection.Face[] = [];

  // Track whether service is active to avoid processing when not needed
  private isActive = false;

  // Animation frame for landmark updates
  private animationFrameId?: number;

  // Asset textures for filter effects
  private filterTextures: Record<string, Texture> = {};

  // Container for face overlays
  private overlayContainer: Container | null = null;

  public constructor() {
    // Load the model when the service is created
    void this.loadModel();
  }

  /**
   * Gets an observable for model loading state
   */
  public get modelLoaded(): Observable<boolean> {
    return this.modelLoaded$.asObservable();
  }

  /**
   * Activates the service - call this when face detection is needed
   */
  public activate(): void {
    this.isActive = true;
  }

  /**
   * Deactivates the service - call this when face detection is no longer needed
   */
  public deactivate(): void {
    this.isActive = false;
    this.stopDetectionLoop();
  }

  /**
   * Load textures for face filter effects
   */
  public loadFilterTextures() {
    try {
      // Load the necessary textures for our filters
      // We'll use SVG strings initially for simplicity, but you can replace with actual image files

      // Face Mask SVG
      const maskSvg = `
        <svg width="300" height="300" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
          <rect x="90" y="100" width="120" height="100" rx="20" fill="#ff69b4" opacity="0.7"/>
        </svg>
      `;

      // Horns SVG
      const hornsSvg = `
        <svg width="300" height="300" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
          <path d="M100,80 L80,20 L110,80 Z" fill="red"/>
          <path d="M200,80 L220,20 L190,80 Z" fill="red"/>
        </svg>
      `;

      // Halo SVG
      const haloSvg = `
        <svg width="300" height="300" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="150" cy="40" rx="50" ry="15" fill="yellow" opacity="0.8"/>
        </svg>
      `;

      // Convert SVGs to data URLs
      const svgToDataURL = (svg: string) => {
        const encodedSvg = encodeURIComponent(svg);
        return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
      };

      // Create textures from data URLs
      this.filterTextures['mask'] = Texture.from(svgToDataURL(maskSvg));
      this.filterTextures['horns'] = Texture.from(svgToDataURL(hornsSvg));
      this.filterTextures['halo'] = Texture.from(svgToDataURL(haloSvg));

      this.logService.info('Loaded face filter textures');
    } catch (error) {
      this.logService.error('Failed to load filter textures', error);
    }
  }

  /**
   * Load the face landmarks detection model
   */
  private async loadModel(): Promise<void> {
    if (this.detector || this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      // Ensure TensorFlow.js is using WebGL backend for best performance
      await tf.setBackend('webgl');

      // Set memory growth to true to avoid OOM errors
      tf.env().set('WEBGL_DELETE_TEXTURE_THRESHOLD', 0);
      tf.env().set('WEBGL_FORCE_F16_TEXTURES', true);

      // Warm up the model
      await tf.ready();

      // Load the face landmarks detection model
      this.detector = await faceLandmarksDetection.createDetector(
        faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh,
        {
          runtime: 'tfjs',
          refineLandmarks: true,
          maxFaces: 1,
        },
      );

      // Also load textures
      await this.loadFilterTextures();

      this.ngZone.run(() => {
        this.modelLoaded$.next(true);
        this.isLoading = false;
        this.logService.info('Face landmarks model loaded successfully');
      });
    } catch (error) {
      this.logService.error('Failed to load face landmarks model', error);
      this.isLoading = false;
      this.ngZone.run(() => {
        this.modelLoaded$.next(false);
      });
    }
  }

  /**
   * Get current face landmarks
   */
  public getFaceLandmarks(): faceLandmarksDetection.Face[] {
    return this.currentFaceLandmarks;
  }

  /**
   * Detect face landmarks in a video frame
   * @param videoElement The video element to process
   */
  public async detectFaceLandmarks(videoElement: HTMLVideoElement): Promise<faceLandmarksDetection.Face[] | null> {
    if (!this.isActive || !this.detector || !videoElement || videoElement.paused || videoElement.ended) {
      return null;
    }

    try {
      // Use tf.tidy to automatically dispose tensors to prevent memory leaks
      const faces = await this.detector.estimateFaces(videoElement);

      // Store current landmarks
      this.currentFaceLandmarks = faces;

      return faces;
    } catch (error) {
      this.logService.error('Error detecting face landmarks', error);
      return null;
    }
  }

  /**
   * Start the face landmarks detection loop
   * @param videoElement The video element to process
   * @param sprite The sprite to apply the effect to
   * @param filterType The type of filter to apply
   * @param renderer The PIXI renderer
   */
  public async startDetectionLoop(videoElement: HTMLVideoElement, sprite: Sprite, filterType: string): Promise<void> {
    if (!this.isActive) {
      this.activate();
    }

    this.logService.info(`FLS: Starting detection loop for filter type: ${filterType}`);
    this.logService.info(`FLS: Video element dimensions: ${videoElement.videoWidth}x${videoElement.videoHeight}`);
    this.logService.info(`FLS: Sprite dimensions: ${sprite.width}x${sprite.height}`);

    // Make sure model is loaded
    if (!this.detector) {
      this.logService.warn('FLS: Face detection model not loaded yet, attempting to load...');
      try {
        await this.loadModel();
        if (!this.detector) {
          this.logService.error('FLS: Failed to load face detection model after retry');
          return;
        }
      } catch (err) {
        this.logService.error('FLS: Error loading model during detection loop start', err);
        return;
      }
    }

    // Create overlay container if it doesn't exist
    if (!this.overlayContainer) {
      this.overlayContainer = new Container();

      // Create a background Graphics object to establish the container dimensions
      const background = new Graphics();
      background.beginFill(0x000000, 0); // Transparent fill
      background.drawRect(0, 0, sprite.width, sprite.height);
      background.endFill();

      // Add the background to the container to establish size
      this.overlayContainer.addChild(background);

      this.logService.info(`FLS: Created overlay container with dimensions ${sprite.width}x${sprite.height}`);

      // Add the overlay container to the sprite
      sprite.addChild(this.overlayContainer);

      // Verify the container was added correctly
      this.logService.info(
        `FLS: After adding to sprite - container parent: ${this.overlayContainer.parent === sprite ? 'correct' : 'WRONG'}`,
      );
      this.logService.info(`FLS: Sprite has ${sprite.children.length} children`);
    } else {
      // Clear existing overlays but keep the background
      while (this.overlayContainer.children.length > 1) {
        this.overlayContainer.removeChildAt(1);
      }

      // Update background dimensions
      if (this.overlayContainer.children.length > 0) {
        const background = this.overlayContainer.getChildAt(0) as Graphics;
        background.clear();
        background.beginFill(0x000000, 0);
        background.drawRect(0, 0, sprite.width, sprite.height);
        background.endFill();
      }

      this.logService.info(`FLS: Updated overlay container dimensions to ${sprite.width}x${sprite.height}`);

      // Check if container is still attached to sprite
      if (this.overlayContainer.parent !== sprite) {
        this.logService.warn('FLS: Container lost its parent! Reattaching to sprite.');
        sprite.addChild(this.overlayContainer);
      }
    }

    // Test rendering a visible graphics object to verify the container is visible
    const testGraphics = new Graphics();
    testGraphics.beginFill(0xff0000); // Red fill
    testGraphics.drawRect(sprite.width / 2 - 50, sprite.height / 2 - 50, 100, 100);
    testGraphics.endFill();
    this.overlayContainer.addChild(testGraphics);
    this.logService.info('FLS: Added test red square to verify container visibility');

    // Process one frame immediately
    await this.processVideoFrameForFaceEffects(videoElement, filterType);

    // Start the detection loop
    const updateDetection = async () => {
      if (!this.isActive) {
        this.logService.info('FLS: Detection loop stopped (not active)');
        return;
      }

      await this.processVideoFrameForFaceEffects(videoElement, filterType);
      this.animationFrameId = requestAnimationFrame(updateDetection);
    };

    // Run the loop outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      this.animationFrameId = requestAnimationFrame(updateDetection);
      this.logService.info('FLS: Face detection animation frame loop started');
    });
  }

  /**
   * Process a single video frame for face detection and apply effects
   * @param videoElement The video element to process
   * @param filterType The type of filter to apply
   */
  private async processVideoFrameForFaceEffects(videoElement: HTMLVideoElement, filterType: string): Promise<void> {
    try {
      if (!this.detector) {
        this.logService.warn('FLS: Face detector not available during frame processing');
        return;
      }

      if (!videoElement || videoElement.paused || videoElement.ended) {
        this.logService.debug('FLS: Video element not ready for processing');
        return;
      }

      if (!this.overlayContainer) {
        this.logService.warn('FLS: Overlay container not available during processing');
        return;
      }

      // Check if video is actually streaming data
      if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
        this.logService.warn('FLS: Video has zero dimensions, cannot process face detection');
        return;
      }

      // Attempt to detect faces
      this.logService.debug(`FLS: Detecting faces on video ${videoElement.videoWidth}x${videoElement.videoHeight}`);
      const faces = await this.detectFaceLandmarks(videoElement);

      if (!faces || faces.length === 0) {
        // This is normal, no need for an error
        this.logService.debug('FLS: No faces detected in this frame');
        return;
      }

      // Log success for debugging
      this.logService.debug(`FLS: Detected ${faces.length} face(s) - applying ${filterType} effect`);

      // Clear previous overlays but keep the background graphics object which maintains dimensions
      while (this.overlayContainer.children.length > 1) {
        this.overlayContainer.removeChildAt(1);
      }

      // Get the first face
      const face = faces[0];

      // Log face details for debugging
      const box = face.box;
      this.logService.debug(
        `FLS: Face detected at (${box.xMin.toFixed(2)},${box.yMin.toFixed(2)}) size: ${box.width.toFixed(2)}x${box.height.toFixed(2)}`,
      );

      // Apply effect based on filter type
      switch (filterType) {
        case 'faceMask':
          this.applyMaskEffect(face);
          break;
        case 'faceHorns':
          this.applyHornsEffect(face);
          break;
        case 'faceHalo':
          this.applyHaloEffect(face);
          break;
        default:
          this.logService.warn(`FLS: Unknown filter type: ${filterType}`);
      }
    } catch (error) {
      this.logService.error('FLS: Error processing video frame for face effects', error);
    }
  }

  /**
   * Apply mask effect
   */
  private applyMaskEffect(face: faceLandmarksDetection.Face): void {
    if (!this.overlayContainer || !this.filterTextures['mask']) {
      this.logService.warn('FLS: Cannot apply mask effect - missing container or texture');
      return;
    }

    try {
      // Create a sprite for the mask
      const maskSprite = new Sprite(this.filterTextures['mask']);

      // Position based on face mesh
      const boundingBox = face.box;
      const parentWidth = this.overlayContainer.width;
      const parentHeight = this.overlayContainer.height;

      this.logService.debug(`FLS: Applying mask with container dimensions: ${parentWidth}x${parentHeight}`);

      // Scale mask to match face width with some padding
      const scaleFactor = 1.2; // Add 20% padding
      maskSprite.width = boundingBox.width * parentWidth * scaleFactor;
      maskSprite.height = boundingBox.height * parentHeight * 0.7; // Cover just the lower part of face

      // Position mask at the center of the face
      maskSprite.x = boundingBox.xMin * parentWidth + (boundingBox.width * parentWidth - maskSprite.width) / 2;
      maskSprite.y = boundingBox.yMin * parentHeight + boundingBox.height * parentHeight * 0.4; // Lower on the face

      this.logService.debug(
        `FLS: Applied mask at (${maskSprite.x.toFixed(0)},${maskSprite.y.toFixed(0)}) size: ${maskSprite.width.toFixed(0)}x${maskSprite.height.toFixed(0)}`,
      );

      // Add mask to overlay container
      this.overlayContainer.addChild(maskSprite);
    } catch (error) {
      this.logService.error('FLS: Error applying mask effect', error);
    }
  }

  /**
   * Apply horns effect
   */
  private applyHornsEffect(face: faceLandmarksDetection.Face): void {
    if (!this.overlayContainer || !this.filterTextures['horns']) {
      this.logService.warn('FLS: Cannot apply horns effect - missing container or texture');
      return;
    }

    try {
      // Create a sprite for the horns
      const hornsSprite = new Sprite(this.filterTextures['horns']);

      // Position based on face mesh
      const boundingBox = face.box;
      const parentWidth = this.overlayContainer.width;
      const parentHeight = this.overlayContainer.height;

      this.logService.debug(`FLS: Applying horns with container dimensions: ${parentWidth}x${parentHeight}`);

      // Scale horns to match face width
      hornsSprite.width = boundingBox.width * parentWidth * 1.2;
      hornsSprite.height = boundingBox.height * parentHeight * 0.6;

      // Position horns at the top of the head
      hornsSprite.x = boundingBox.xMin * parentWidth + (boundingBox.width * parentWidth - hornsSprite.width) / 2;
      hornsSprite.y = boundingBox.yMin * parentHeight - hornsSprite.height * 0.3;

      this.logService.debug(
        `FLS: Applied horns at (${hornsSprite.x.toFixed(0)},${hornsSprite.y.toFixed(0)}) size: ${hornsSprite.width.toFixed(0)}x${hornsSprite.height.toFixed(0)}`,
      );

      // Add horns to overlay container
      this.overlayContainer.addChild(hornsSprite);
    } catch (error) {
      this.logService.error('FLS: Error applying horns effect', error);
    }
  }

  /**
   * Apply halo effect
   */
  private applyHaloEffect(face: faceLandmarksDetection.Face): void {
    if (!this.overlayContainer || !this.filterTextures['halo']) {
      this.logService.warn('FLS: Cannot apply halo effect - missing container or texture');
      return;
    }

    try {
      // Create a sprite for the halo
      const haloSprite = new Sprite(this.filterTextures['halo']);

      // Position based on face mesh
      const boundingBox = face.box;
      const parentWidth = this.overlayContainer.width;
      const parentHeight = this.overlayContainer.height;

      this.logService.debug(`FLS: Applying halo with container dimensions: ${parentWidth}x${parentHeight}`);

      // Scale halo proportionally to face width
      haloSprite.width = boundingBox.width * parentWidth * 1.3;
      haloSprite.height = boundingBox.width * parentWidth * 0.3; // Keep it oval-shaped

      // Position halo above the head
      haloSprite.x = boundingBox.xMin * parentWidth + (boundingBox.width * parentWidth - haloSprite.width) / 2;
      haloSprite.y = boundingBox.yMin * parentHeight - haloSprite.height * 1.2;

      this.logService.debug(
        `FLS: Applied halo at (${haloSprite.x.toFixed(0)},${haloSprite.y.toFixed(0)}) size: ${haloSprite.width.toFixed(0)}x${haloSprite.height.toFixed(0)}`,
      );

      // Add halo to overlay container
      this.overlayContainer.addChild(haloSprite);
    } catch (error) {
      this.logService.error('FLS: Error applying halo effect', error);
    }
  }

  /**
   * Stop the detection loop
   */
  private stopDetectionLoop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }

    // Clean up overlay container
    if (this.overlayContainer) {
      this.overlayContainer.removeChildren();
    }
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.stopDetectionLoop();

    // Clean up TensorFlow resources
    if (tf.getBackend() === 'webgl') {
      const engine = tf.engine();
      engine.disposeVariables();
      tf.dispose();
    }

    // Clear detection model
    this.detector = null;

    // Clean up textures
    Object.values(this.filterTextures).forEach((texture) => {
      texture.destroy();
    });
    this.filterTextures = {};

    // Clear overlay container
    if (this.overlayContainer) {
      this.overlayContainer.destroy({ children: true });
      this.overlayContainer = null;
    }
  }

  /**
   * Angular lifecycle hook for cleanup
   */
  public ngOnDestroy(): void {
    this.destroy();
  }
}
